// Quaternion math for fusing iOS DeviceOrientation samples into stable
// orientation captures and computing the relative rotation between two
// captures.
//
// iOS DeviceOrientationEvent semantics (per spec):
//   alpha — rotation around Z-axis (out of screen), 0..360°
//   beta  — rotation around X-axis (right of screen), -180..180°
//   gamma — rotation around Y-axis (top of screen), -90..90°
// Composition order (intrinsic Tait–Bryan): R = Rz(alpha) · Rx(beta) · Ry(gamma).

export interface Quat {
  w: number;
  x: number;
  y: number;
  z: number;
}

const DEG = Math.PI / 180;
const RAD = 180 / Math.PI;

/* ─── Constructors ─── */

export function quatFromEuler(alpha: number, beta: number, gamma: number): Quat {
  // Half-angles in radians
  const a = (alpha * DEG) / 2;
  const b = (beta * DEG) / 2;
  const g = (gamma * DEG) / 2;
  const cA = Math.cos(a), sA = Math.sin(a);
  const cB = Math.cos(b), sB = Math.sin(b);
  const cG = Math.cos(g), sG = Math.sin(g);

  // qZ(α) · qX(β) · qY(γ)
  // Worked out by composing qZ * qX, then * qY.
  return {
    w: cA * cB * cG - sA * sB * sG,
    x: cA * sB * cG - sA * cB * sG,
    y: cA * cB * sG + sA * sB * cG,
    z: cA * sB * sG + sA * cB * cG,
  };
}

/* ─── Algebra ─── */

export function quatConjugate(q: Quat): Quat {
  return { w: q.w, x: -q.x, y: -q.y, z: -q.z };
}

export function quatMultiply(a: Quat, b: Quat): Quat {
  return {
    w: a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z,
    x: a.w * b.x + a.x * b.w + a.y * b.z - a.z * b.y,
    y: a.w * b.y - a.x * b.z + a.y * b.w + a.z * b.x,
    z: a.w * b.z + a.x * b.y - a.y * b.x + a.z * b.w,
  };
}

export function quatNormalise(q: Quat): Quat {
  const n = Math.hypot(q.w, q.x, q.y, q.z);
  if (n === 0) return { w: 1, x: 0, y: 0, z: 0 };
  return { w: q.w / n, x: q.x / n, y: q.y / n, z: q.z / n };
}

/* ─── Relative rotation ─── */

/**
 * Quaternion describing the rotation that takes `start` orientation to `end`.
 *   q_rel = q_end · q_start⁻¹
 * For unit quaternions, conjugate == inverse.
 */
export function relativeRotation(start: Quat, end: Quat): Quat {
  return quatNormalise(quatMultiply(end, quatConjugate(start)));
}

/**
 * Total rotation angle (geodesic) encoded in a unit quaternion, in degrees.
 * Returned in [0, 180].
 */
export function quatAngleDeg(q: Quat): number {
  const w = Math.max(-1, Math.min(1, Math.abs(q.w)));
  return 2 * Math.acos(w) * RAD;
}

/**
 * Unit rotation axis encoded in a quaternion (in device coordinates:
 * x = right of screen, y = top of screen, z = out of screen).
 * If the rotation is ~zero, returns the X axis as a stable default.
 */
export function quatAxis(q: Quat): { x: number; y: number; z: number } {
  const sinHalf = Math.sqrt(Math.max(0, 1 - q.w * q.w));
  if (sinHalf < 1e-6) return { x: 1, y: 0, z: 0 };
  const s = q.w < 0 ? -sinHalf : sinHalf; // flip so the angle stays positive
  return { x: q.x / s, y: q.y / s, z: q.z / s };
}

/**
 * Decompose a small relative rotation into per-device-axis deltas (degrees).
 * Useful for showing which axis dominated — e.g. "rotation mostly around X"
 * tells the user the bone's long axis was aligned with the phone's short edge.
 */
export function axisBreakdownDeg(q: Quat): { x: number; y: number; z: number } {
  const angle = quatAngleDeg(q);
  const axis = quatAxis(q);
  return {
    x: axis.x * angle,
    y: axis.y * angle,
    z: axis.z * angle,
  };
}

/* ─── Gravity-based screen roll (for the live goniometer) ─── */

/**
 * The world gravity vector (0, 0, −1) expressed in the device's own frame.
 * Useful for "spirit-level" / inclinometer use because it's gravity-referenced
 * (no compass, no magnetometer — both unreliable in the OR).
 *
 * Returns components along device X (right of screen), Y (top of screen),
 * Z (out of screen toward the user). Magnitude ≈ 1 for unit quaternions.
 */
export function gravityInDeviceFrame(q: Quat): {
  gx: number;
  gy: number;
  gz: number;
} {
  // World gravity = (0, 0, -1). Rotate by qᵀ (= q⁻¹ for unit q) into device.
  // Equivalent to extracting -1 × (third row of the rotation matrix from q).
  const { w, x, y, z } = q;
  return {
    gx: 2 * (w * y - x * z),
    gy: -(2 * y * z + 2 * w * x),
    gz: 2 * (x * x + y * y) - 1,
  };
}

/**
 * The phone's roll angle in its own screen plane, gravity-referenced.
 * 0° when the phone is held vertically with its bottom edge pointing down.
 * Positive when tilted clockwise (from the user's point of view), negative
 * when tilted counter-clockwise. Range: (-180, 180].
 *
 * Degenerate when the phone is nearly face-up or face-down — gx and gy both
 * approach zero, atan2 jitters wildly. Caller should check |gz| < ~0.95
 * before trusting the value.
 */
export function screenRollDeg(q: Quat): number {
  const { gx, gy } = gravityInDeviceFrame(q);
  return (Math.atan2(-gx, -gy) * 180) / Math.PI;
}

/**
 * Smallest signed difference between two angles, accounting for the
 * 180/-180 wrap-around. Result ∈ (-180, 180].
 */
export function deltaAngleDeg(current: number, reference: number): number {
  let d = current - reference;
  while (d > 180) d -= 360;
  while (d <= -180) d += 360;
  return d;
}

/* ─── Sample averaging ─── */

/**
 * Stable mean of multiple unit quaternions. Uses the standard "flip into the
 * same hemisphere as the first sample, then component-wise mean and
 * renormalise" trick — accurate enough for the small spread observed during
 * an 800 ms capture window.
 */
export function quatMean(samples: Quat[]): Quat {
  if (samples.length === 0) return { w: 1, x: 0, y: 0, z: 0 };
  const ref = samples[0];
  let sw = 0, sx = 0, sy = 0, sz = 0;
  for (const s of samples) {
    const d = ref.w * s.w + ref.x * s.x + ref.y * s.y + ref.z * s.z;
    const sgn = d < 0 ? -1 : 1;
    sw += sgn * s.w;
    sx += sgn * s.x;
    sy += sgn * s.y;
    sz += sgn * s.z;
  }
  return quatNormalise({ w: sw, x: sx, y: sy, z: sz });
}
